import S, { DataSignal } from 's-js';
import UOM from 'lib/UOM';
import { zipMap } from 'lib/zipMap';

import { Measurement, ImmutableMeasurement } from 'data/models/Measurement';
import { SetPierGroupFootmarksRequest, SetBerthFootmarksRequest } from 'data/models/SetPierGroupFootmarksRequest';

import { PCCache } from 'data/PCCache';

import { ToggleableMeasurement } from 'portcall/toggleablemeasurement/model';

const LENGTH_CONVERSIONS = [{
    uom: UOM.Foot,
    decimalPlaces: 0
}, {
    uom: UOM.Meter,
    decimalPlaces: 1
}];

export interface PierGroupFootmarksParams {
    req : SetPierGroupFootmarksRequest;
    pierLengthUOM : () => string | null;
    displayUOM : () => string;
}

export type PierGroupFootmarks = ReturnType<typeof PierGroupFootmarks>;
export function PierGroupFootmarks(
    data : PCCache,
    { req, pierLengthUOM, displayUOM } : PierGroupFootmarksParams
) {
    const
        viewModelsToBerthReqs = new WeakMap<BerthFootmarks, SetBerthFootmarksRequest>(),
        lastBerthReq = () => req.Berths().length > 0 ? req.Berths()[req.Berths().length - 1] : undefined,
        Berths = zipMap(
            req.Berths,
            // Remove last berth and align w/ right edge of original Berths array. Has the effect of pairing
            // each berth with the previous berth.
            { arr: () => req.Berths().slice(0, req.Berths().length - 1), align: 'right' },
            (berthReq, prevReq) => {
                const vm = BerthFootmarks(
                    berthReq,
                    prevReq,
                    lastBerthReq as () => SetBerthFootmarksRequest,
                    data,
                    displayUOM
                );
                viewModelsToBerthReqs.set(vm, berthReq);
                return vm
            }
        ),
        uom = () => pierLengthUOM() ?? UOM.Foot;

    const pgLength = new Measurement();
    pgLength.Num = ((val? : number) => {
        if (val === undefined) {
            // When getting value, add up new berth lengths
            const totalLength = req.Berths().reduce(
                (sum, berthReq) => sum.add(berthReq.Length),
                new Measurement({ Num: 0, UOM: uom() })
            );
            return totalLength.Num();
        } else {
            // When setting value, set last berth length
            S.sample(() => {
                const
                    allReqs = req.Berths(),
                    lastReq = allReqs[allReqs.length - 1];
                if (lastReq) {
                    const
                        allReqsButLast = allReqs.slice(0, allReqs.length - 1),
                        lengthExcludingLast = allReqsButLast.reduce(
                            (sum, berthReq) => sum.add(berthReq.Length),
                            new Measurement({ Num: 0, UOM: uom() })
                        );
                    lastReq.Length.set(new Measurement({ Num: val, UOM: uom() }).subtract(lengthExcludingLast));
                }
                return val;
            });
        }
    }) as DataSignal<number>;
    pgLength.UOM = uom;

    return {
        Berths,
        OnSortBerths,
        PierGroupLength: ToggleableMeasurement({
            backingMeasurement: pgLength,
            uom: displayUOM,
            conversions: LENGTH_CONVERSIONS
        }),
        // By definition, last berth length is valid when there are no berths (edge case).
        LastBerthLengthIsValid: () => Berths().length === 0 || Berths()[Berths().length - 1].LengthIsValid(),
        FootmarksAreValid: () => Berths().every(b => b.LengthIsValid())
    };

    function OnSortBerths(_moved : BerthFootmarks, berthFootmarkses : BerthFootmarks[]) {
        S.sample(() => S.freeze(() => {
            const newBerthReqs = berthFootmarkses.map(vm => viewModelsToBerthReqs.get(vm)!);
            // Keep the overall StartMark for the piergroup fixed when reordering berths.
            // This value will then cascade through the rest of the berths.
            if (newBerthReqs.length && req.Berths().length) {
                newBerthReqs[0].StartMark.set(req.Berths()[0].StartMark);
            }
            req.Berths(newBerthReqs);
        }));
    }
}

export type BerthFootmarks = ReturnType<typeof BerthFootmarks>;
function BerthFootmarks(
    berthReq : SetBerthFootmarksRequest,
    prevReq : () => SetBerthFootmarksRequest | undefined,
    // Can't be undefined b/c as long as we have at least one berth footmarks request, we will
    // also have a LAST berth footmarks request.
    lastReq : () => SetBerthFootmarksRequest,
    data : PCCache,
    displayUOM : () => string
) {
    // In the UI, we'll present a control for editing StartMark, but we'll actually translate
    // that into an edit to one or more berth Lengths. That way, changes always propagate
    // from one berth's Length to the next berth's StartMark, rather than vice versa. Means
    // that if the user reorders berths, then the Lengths stay fixed and the StartMarks update,
    // which is a nicer UX than keeping the StartMarks fixed and making the Lengths update.
    const startMark = new Measurement();
    startMark.Num = ((val? : number) => {
        if (val === undefined) {
            return berthReq.StartMark.Num();
        } else {
            S.sample(() => S.freeze(() => {
                if (!prevReq()) {
                    // For initial berths, make sure to set StartMark here as we won't set it otherwise. For
                    // non-initial berths, COULD set StartMark here, but we already have a separate S
                    // computation that sets this berth's StartMark from the previous berth's StartMark and
                    // Length, so we'll let that handle updating the StartMark.
                    berthReq.StartMark.Num(val);
                }
                // Then, for all berths, find the previous berth, rotating around to the last berth if this is
                // the initial berth....
                const prevWithRotation = prevReq() ?? lastReq();
                // ...And make sure that previous berth is DIFFERENT from the current berth. That rules out the
                // case where we only have one berth. The logic below would try to increment and decrement that
                // one berth's Length by the same amounts, which would logically be a no-op except that we'd run
                // into S's restriction against setting the same S.value to two different values in the same
                // clock tick...
                if (prevWithRotation !== berthReq) {
                    // ...And if we found a different previous berth, decrement THIS berth's Length by the delta
                    // in StartMark values and increment the previous berth's Length by the same delta. The
                    // effect is that editing one StartMark value doesn't change any other StartMark values or
                    // the overall piergroup length, it just redistributes the piergroup length among the various
                    // berths in the piergroup.
                    // NOTE: We're reading the same StartMark.Num signal that we may have set above (for initial
                    // berths only). That's OK. We'll read the old value here, as we're in a big S.freeze block,
                    // so the new value won't have taken effect yet.
                    const delta = new Measurement({ Num: val - berthReq.StartMark.Num(), UOM: berthReq.StartMark.UOM() });
                    berthReq.Length.set(berthReq.Length.subtract(delta));
                    prevWithRotation.Length.set(prevWithRotation.Length.add(delta));
                }
                return val;
            }));
        }
    }) as DataSignal<number>;
    // Don't try to support changing UOM.
    startMark.UOM = () => berthReq.StartMark.UOM();

    // Set this request's StartMark base on previous request's StartMark and Length
    S(() => {
        const prev = prevReq();
        if (prev) {
            const newStartMark = prev.StartMark.add(prev.Length);
            S(() => {
                // Hmm... Measurement.prototype.set samples the passed-in Measurement's Num and
                // UOM, otherwise it'd be perfect here. Maybe worth revising .set not to sample,
                // so calling code can call directly if it doesn't want to sample or wrap the call
                // in S.sample(...) if it does?
                berthReq.StartMark.Num(newStartMark.Num());
                berthReq.StartMark.UOM(newStartMark.UOM());
            });
        }
    });

    return {
        Berth: data.Berths.update1(null, { Id: berthReq.Id() }),
        StartMark: ToggleableMeasurement({
            backingMeasurement: startMark,
            uom: displayUOM,
            conversions: LENGTH_CONVERSIONS
        }),
        Length: ToggleableMeasurement({
            backingMeasurement: new ImmutableMeasurement({
                Num: () => berthReq.Length.Num(),
                UOM: () => berthReq.Length.UOM()
            }),
            uom: displayUOM,
            conversions: LENGTH_CONVERSIONS
        }),
        // Just need lengths to be nonnegative. Zero we accept as a workaround for the fact
        // that we currently don't allow deleting berths (still need to figure out what the
        // implications for existing PortCalls would be).
        LengthIsValid: () => berthReq.Length.Num() >= 0,
        // By definition, PrevLength is valid when there's no previous request.
        PrevLengthIsValid: () => (prevReq()?.Length.Num() ?? 0) >= 0
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/piergroupfootmarks/model.ts